import React, { useCallback, useMemo } from "react";
import { noop } from "@common/helpers/function";
import { CatalogItemFilterType } from "@common/types/catalogItem";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { useCatalogItems } from "@api/catalogItem.api";
import { ModeRadioGroup } from "../ReviewFilter/ModeRadioGroup";
import { BaseFilterProps } from "../../lib";
import styles from "./CatalogItemFilter.module.scss";

const MODE_OPTIONS: Array<{
    value: CatalogItemFilterType;
    label: string;
}> = [
    { value: CatalogItemFilterType.ANY_SELECTED, label: "Any selected" },
    { value: CatalogItemFilterType.ALL_SELECTED, label: "All selected" },
    { value: CatalogItemFilterType.EXCLUDE_SELECTED, label: "Exclude" },
];

export const CatalogItemFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const { items } = useCatalogItems();

    const options = useMemo<FilterMultiselectOption[]>(
        () =>
            items.map((item) => ({
                value: item.id,
                label: item.name,
                searchableLabel: item.name,
            })),
        [items],
    );

    const value = filters.catalogItemIds ?? [];
    const mode =
        filters.catalogItemFilterType ?? CatalogItemFilterType.ANY_SELECTED;

    const handleModeChange = useCallback(
        (newMode: CatalogItemFilterType) => {
            onChange({ catalogItemFilterType: newMode });
        },
        [onChange],
    );

    const handleChange = useCallback(
        (val: Array<string | number>) => {
            onChange({
                catalogItemIds: val.length > 0 ? val.map(String) : undefined,
            });
        },
        [onChange],
    );

    return (
        <div className={styles.filter}>
            <ModeRadioGroup
                name="catalogItemFilterType"
                value={mode}
                options={MODE_OPTIONS}
                onChange={handleModeChange}
            />
            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    options={options}
                    value={value}
                    onChange={handleChange}
                    name="catalogItemIds"
                    onBlur={noop}
                    filterFn={(search, option) => {
                        const query = search.toLowerCase();
                        const searchTarget =
                            option.searchableLabel ??
                            String(option.label ?? "");
                        return searchTarget.toLowerCase().includes(query);
                    }}
                />
            </div>
        </div>
    );
};
